import React from "react";
import MoveSectionNode from "./Nodes/MoveSectionNode";
import { compareSiblingOrders } from "./Nodes/utils";

/**
 * MoveNodesRenderer Component
 * 
 * Renders document nodes for move position selection from scratch
 * Implements complete node hierarchy: Sections -> Steps -> Substeps -> Child Nodes
 * 
 * Checkbox disabling rules:
 * - If moving a section: all nodes (steps, substeps, child nodes) have disabled checkboxes
 * - If moving a step: substeps and child nodes have disabled checkboxes
 * - If moving a substep: child nodes have disabled checkboxes
 * 
 * @param {Array} preloadedData - Array of section data to render
 * @param {Function} customOnNavigate - Custom handler for checkbox clicks
 * @param {Object} checkboxState - Checkbox state control
 * @param {string} moveNodeType - Type of node being moved ('section' | 'step' | 'substep' | 'attachment' | 'video' | 'rich-text' | 'form' | 'canva' | 'canvas')
 * @param {string} checkboxLabelColor - Optional color for checkbox labels
 */
const MoveNodesRenderer = ({
  preloadedData = [],
  customOnNavigate,
  checkboxState,
  moveNodeType,
  checkboxLabelColor = "rgb(0, 102, 204)",
}) => {
  // Get source node ID from checkboxState
  const sourceNodeId = checkboxState?.sourceNodeId;
  
  // Debug: Log source node info (hooks must be called before early returns)
  React.useEffect(() => {
    if (sourceNodeId && moveNodeType && preloadedData?.length > 0) {
    }
  }, [sourceNodeId, moveNodeType, checkboxState, preloadedData]);

  // Handle empty or null data
  if (!preloadedData || preloadedData.length === 0) {
    return null;
  }
  

  // Sort sections by sibling_order
  const sortedSections = [...preloadedData].sort((a, b) => {
    const orderA = a.sibling_order || a.procedure_tree?.sibling_order;
    const orderB = b.sibling_order || b.procedure_tree?.sibling_order;
    const orderCompare = compareSiblingOrders(orderA, orderB);
    if (orderCompare !== 0) return orderCompare;
    
    // Fallback: compare created_at
    const dateA = new Date(a.created_at);
    const dateB = new Date(b.created_at);
    return dateB - dateA; // latest comes first
  });

  // Calculate total steps before each section for global step numbering
  const getGlobalStepStart = (sectionIndex) => {
    let totalSteps = 0;
    for (let i = 0; i < sectionIndex; i++) {
      totalSteps += sortedSections[i]?.steps?.length || 0;
    }
    return totalSteps;
  };

  // Helper to find section by ID
  const findSectionById = (sectionId) => {
    return preloadedData.find(s => s.tree_node_id === sectionId);
  };

  // Helper to find step by ID
  const findStepById = (stepId) => {
    for (const section of preloadedData) {
      const step = section.steps?.find(s => s.tree_node_id === stepId || s.step_id_ref === stepId);
      if (step) return { step, section };
    }
    return null;
  };

  // Helper to find substep by ID
  const findSubstepById = (substepId) => {
    for (const section of preloadedData) {
      for (const step of section.steps || []) {
        const substep = step.substeps?.find(s => s.tree_node_id === substepId || s.substep_id_ref === substepId);
        if (substep) return { substep, step, section };
      }
    }
    return null;
  };

  // Check if a section is the source section being moved
  const isSourceSection = (sectionId) => {
    if (moveNodeType !== 'section') return false;
    return checkboxState?.sourceNodeId === sectionId;
  };

  // Check if a step is the source step being moved
  const isSourceStep = (stepId, stepIdRef) => {
    if (moveNodeType !== 'step') return false;
    return checkboxState?.sourceNodeId === stepId || checkboxState?.sourceNodeId === stepIdRef;
  };

  // Check if a substep is the source substep being moved
  const isSourceSubstep = (substepId, substepIdRef) => {
    if (moveNodeType !== 'substep') return false;
    return checkboxState?.sourceNodeId === substepId || checkboxState?.sourceNodeId === substepIdRef;
  };

  // Check if a step belongs to the source section
  const isStepInSourceSection = (stepId, sectionId) => {
    if (moveNodeType !== 'section') return false;
    if (!sourceNodeId) return false;
    if (sectionId !== sourceNodeId) return false;
    // Check if this step belongs to the source section
    const section = findSectionById(sectionId);
    if (!section) return false;
    return section.steps?.some(s => s.tree_node_id === stepId || s.step_id_ref === stepId) || false;
  };

  // Check if a substep belongs to the source section or step
  const isSubstepInSourceHierarchy = (substepId, stepId, stepIdRef, sectionId) => {
    if (moveNodeType === 'section') {
      if (!sourceNodeId || sectionId !== sourceNodeId) return false;
      // Check if this substep belongs to a step in the source section
      const section = findSectionById(sectionId);
      if (!section) return false;
      for (const step of section.steps || []) {
        if (step.substeps?.some(s => s.tree_node_id === substepId || s.substep_id_ref === substepId)) {
          return true;
        }
      }
      return false;
    }
    if (moveNodeType === 'step') {
      if (!sourceNodeId) return false;
      if (stepId !== sourceNodeId && stepIdRef !== sourceNodeId) return false;
      // Check if this substep belongs to the source step
      const stepData = findStepById(stepId);
      if (!stepData) return false;
      return stepData.step.substeps?.some(s => s.tree_node_id === substepId || s.substep_id_ref === substepId) || false;
    }
    return false;
  };

  // Check if a child node belongs to the source hierarchy
  // This checks if the child's parent (step or substep) belongs to the source node
  const isChildInSourceHierarchy = (child, stepId, stepIdRef, substepId, substepIdRef, sectionId) => {
    if (!sourceNodeId) return false;
    
    if (moveNodeType === 'section') {
      // For section moves: child belongs if its section is the source section
      return sectionId === sourceNodeId;
    }
    if (moveNodeType === 'step') {
      // For step moves: child belongs if its step is the source step (or if it's in a substep of the source step)
      if (stepId === sourceNodeId || stepIdRef === sourceNodeId) {
        return true; // Child belongs to source step (either directly or via substep)
      }
      return false;
    }
    if (moveNodeType === 'substep') {
      // For substep moves: child belongs if its substep is the source substep
      if (substepId) {
        return substepId === sourceNodeId || substepIdRef === sourceNodeId;
      }
      return false;
    }
    return false;
  };

  // Determine if checkboxes should be shown/disabled based on moveNodeType and source node
  const shouldShowStepCheckbox = (stepId, stepIdRef, sectionId) => {
    if (moveNodeType === 'section') {
      // Show step checkboxes only for steps in source section (but disabled)
      return isStepInSourceSection(stepId, sectionId);
    }
    // For step/substep/child moves, show all step checkboxes
    return true;
  };

  const shouldShowSubstepCheckbox = (substepId, stepId, stepIdRef, sectionId) => {
    if (moveNodeType === 'section') {
      // Show substep checkboxes only for substeps in source section (but disabled)
      return isSubstepInSourceHierarchy(substepId, stepId, stepIdRef, sectionId);
    }
    if (moveNodeType === 'step') {
      // Show substep checkboxes only for substeps in source step (but disabled)
      return isSubstepInSourceHierarchy(substepId, stepId, stepIdRef, sectionId);
    }
    // For substep/child moves, show all substep checkboxes
    return true;
  };

  const shouldShowChildCheckbox = (child, stepId, stepIdRef, substepId, substepIdRef, sectionId) => {
    if (moveNodeType === 'section') {
      // Show child checkboxes only for children in source section (but disabled)
      return isChildInSourceHierarchy(child, stepId, stepIdRef, substepId, substepIdRef, sectionId);
    }
    if (moveNodeType === 'step') {
      // Show child checkboxes only for children in source step (but disabled)
      return isChildInSourceHierarchy(child, stepId, stepIdRef, substepId, substepIdRef, sectionId);
    }
    if (moveNodeType === 'substep') {
      // Show child checkboxes only for children in source substep (but disabled)
      return isChildInSourceHierarchy(child, stepId, stepIdRef, substepId, substepIdRef, sectionId);
    }
    // For child moves, show all child checkboxes
    return true;
  };

  // Check if a step/substep/child checkbox should be disabled (always true for source node's children)
  const shouldDisableStepCheckbox = (stepId, stepIdRef, sectionId) => {
    if (moveNodeType === 'section') {
      // Disable steps in source section
      return isStepInSourceSection(stepId, sectionId);
    }
    return false;
  };

  const shouldDisableSubstepCheckbox = (substepId, stepId, stepIdRef, sectionId) => {
    if (moveNodeType === 'section') {
      // Disable substeps in source section
      return isSubstepInSourceHierarchy(substepId, stepId, stepIdRef, sectionId);
    }
    if (moveNodeType === 'step') {
      // Disable substeps in source step
      return isSubstepInSourceHierarchy(substepId, stepId, stepIdRef, sectionId);
    }
    return false;
  };

  const shouldDisableChildCheckbox = (child, stepId, stepIdRef, substepId, substepIdRef, sectionId) => {
    if (moveNodeType === 'section') {
      // Disable children in source section
      return isChildInSourceHierarchy(child, stepId, stepIdRef, substepId, substepIdRef, sectionId);
    }
    if (moveNodeType === 'step') {
      // Disable children in source step
      return isChildInSourceHierarchy(child, stepId, stepIdRef, substepId, substepIdRef, sectionId);
    }
    if (moveNodeType === 'substep') {
      // Disable children in source substep
      return isChildInSourceHierarchy(child, stepId, stepIdRef, substepId, substepIdRef, sectionId);
    }
    return false;
  };

  return (
    <div>
      {sortedSections.map((section, index) => {
        const globalStepStart = getGlobalStepStart(index);
        return (
          <MoveSectionNode
            key={section.tree_node_id}
            section={section}
            sectionIndex={index}
            globalStepStart={globalStepStart}
            checkboxState={checkboxState}
            moveNodeType={moveNodeType}
            checkboxLabelColor={checkboxLabelColor}
            customOnNavigate={customOnNavigate}
            shouldDisableStepCheckbox={shouldDisableStepCheckbox}
            shouldShowStepCheckbox={shouldShowStepCheckbox}
            shouldDisableSubstepCheckbox={shouldDisableSubstepCheckbox}
            shouldShowSubstepCheckbox={shouldShowSubstepCheckbox}
            shouldDisableChildCheckbox={shouldDisableChildCheckbox}
            shouldShowChildCheckbox={shouldShowChildCheckbox}
            preloadedData={preloadedData}
            compareSiblingOrders={compareSiblingOrders}
          />
        );
      })}
    </div>
  );
};

export default MoveNodesRenderer;
